import { ArrowRight, CheckCircle, Flask, User, WarningCircle } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';

/**
 * 次の一手カード — 「いまどこ / 次にやること / 止まっている理由 / 担当」を
 * 開いて3秒で分かるようにする。画面の主アクションはここに1つだけ置く。
 */
export const NextStepCard = ({ g, stageLabel, nextStage, canEdit, onAdvance, onJump,
  testid = 'dev-next-step' }) => {
  if (!g) return null;
  // ONE list from the backend (`blocker_items`). The banner and the sidebar
  // 試作の開始 block show ONLY items that actually stop 試作; the yellow cards
  // below show everything, including informational guidance. Previously these
  // read three separately-derived lists that disagreed with each other.
  const blockerItems = g.blocker_items || [];
  const hardBlockers = blockerItems.filter((b) => b.blocks_prototype);
  const cannotBuild = hardBlockers.length > 0 || g.parts_ready === false;
  // 2026-10-23 · 次のステージの「満たしていない条件」をそのまま見せる
  // （「条件が満たされていません」だけでは、何を直せばよいか分からなかった）。
  const unmetGates = (nextStage?.gates || []).filter((x) => x.unmet && x.enforced);

  return (
    <section className="rounded-lg border border-zinc-300 bg-white p-5 shadow-sm sm:p-6"
      data-testid={testid}>
      {cannotBuild && (
        <div className="mb-5 rounded-md border-l-4 border-rose-600 bg-rose-50 px-4 py-3"
          data-testid={`${testid}-build-block`}>
          <p className="text-base font-bold text-rose-900">
            {hardBlockers.length === 0 ? '部品がそろうまで試作に投入できません' : '試作を開始できません'}
          </p>
          <p className="mt-0.5 flex flex-wrap gap-x-4 text-sm font-semibold text-rose-900">
            <span data-testid={`${testid}-short-parts`}>不足部品: {g.shortage_parts || 0}</span>
            <span data-testid={`${testid}-missing-docs`}>不足書類: {g.blocking_document_count ?? g.missing_document_count ?? 0}</span>
          </p>
          {hardBlockers.length > 0 && (
            <p className="mt-1 text-sm text-rose-900" data-testid={`${testid}-hard-actions`}>
              次のアクション: {[...new Set(hardBlockers.map((b) => b.next_action))].join(' / ')}
            </p>
          )}
        </div>
      )}
      <div className="flex flex-wrap items-start gap-x-8 gap-y-4">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">いまここ</p>
          <p className="text-lg font-bold text-zinc-900" data-testid={`${testid}-stage`}>{stageLabel}</p>

          <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-zinc-500">次にやること</p>
          <p className="text-base font-bold text-teal-900" data-testid={`${testid}-action`}>
            {g.next_action || '—'}
          </p>

          {blockerItems.length > 0 && (
            <div className="mt-4" data-testid={`${testid}-blockers`}>
              <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
                止まっている理由と次のアクション
              </p>
              <ul className="mt-1 space-y-2">
                {blockerItems.map((b) => (
                  <li key={b.code}
                    className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2"
                    data-testid={`${testid}-blocker-${b.code}`}>
                    <p className="flex items-start gap-1.5 text-sm font-bold text-amber-900">
                      <WarningCircle size={14} className="mt-0.5 shrink-0" />{b.label}
                    </p>
                    {b.detail && <p className="pl-5 text-xs text-amber-900">{b.detail}</p>}
                    <p className="mt-0.5 flex flex-wrap items-center gap-x-3 pl-5 text-xs">
                      <span className={g.owner_missing ? 'font-semibold text-rose-800' : 'text-zinc-700'}>
                        担当者: {b.assignee || g.owner_label}
                      </span>
                      <span className="font-semibold text-teal-900">
                        次のアクション: {b.next_action}
                      </span>
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="w-full shrink-0 space-y-3 sm:w-64">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">担当</p>
            <p className={`flex items-center gap-1.5 text-sm font-semibold ${
              g.owner_missing ? 'text-amber-900' : 'text-zinc-900'}`}
              data-testid={`${testid}-owner`}>
              <User size={14} /> {g.owner_label}
            </p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">試作の開始</p>
            {hardBlockers.length === 0 ? (
              <>
                <p className="flex items-center gap-1.5 text-sm font-semibold text-emerald-700"
                  data-testid={`${testid}-can-build`}>
                  <CheckCircle size={14} weight="fill" /> 試作を始められます
                </p>
                {g.parts_ready === false && (
                  <p className="text-xs text-amber-900" data-testid={`${testid}-parts-pending`}>
                    ただし部品がそろうまで、試作への投入はできません。
                  </p>
                )}
              </>
            ) : (
              <>
                <p className="flex items-center gap-1.5 text-sm font-semibold text-rose-800"
                  data-testid={`${testid}-cannot-build`}>
                  <Flask size={14} /> まだ始められません
                </p>
                <ul className="mt-0.5 space-y-0.5 text-xs text-rose-900">
                  {hardBlockers.map((b) => (
                    <li key={b.code} data-testid={`${testid}-hard-${b.code}`}>・{b.label}</li>
                  ))}
                </ul>
              </>
            )}
          </div>
          {g.next_action === '完了して登録する' && canEdit ? (
            // 2026-11-04 · 完了の条件がそろったら、どのステージでも完了へ案内する
            <Button className="w-full" onClick={() => onJump('dev-completion-card')}
              data-testid={`${testid}-complete-btn`}>
              完了して登録する <ArrowRight size={14} className="ml-1" />
            </Button>
          ) : nextStage && canEdit ? (
            <>
              <Button className="w-full" onClick={() => onAdvance(nextStage.stage)}
                disabled={unmetGates.length > 0}
                data-testid={`${testid}-advance-btn`}>
                次のステップへ進む（{nextStage.label}）<ArrowRight size={14} className="ml-1" />
              </Button>
              {unmetGates.length > 0 && (
                <div className="mt-1 rounded-md border border-amber-300 bg-amber-50 px-2 py-1.5"
                  data-testid={`${testid}-gate-list`}>
                  <p className="text-[11px] font-bold text-amber-900">
                    進むには、あと {unmetGates.length} 件
                  </p>
                  <ul className="mt-0.5 space-y-0.5 text-[11px] text-amber-900">
                    {unmetGates.map((g, i) => (
                      <li key={g.label || i} data-testid={`${testid}-gate-${i}`}>
                        ・{g.label}{g.detail ? `（${g.detail}）` : ''}
                      </li>
                    ))}
                  </ul>
                  <p className="mt-0.5 text-[10px] text-amber-800">
                    管理者はメニューの「例外として進む」で理由を書いて進められます。
                  </p>
                </div>
              )}
            </>
          ) : (
            <Button variant="outline" className="w-full" onClick={() => onJump('dev-parts')}
              data-testid={`${testid}-jump-btn`}>必要部品を見る</Button>
          )}
        </div>
      </div>
    </section>
  );
};
